/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { formatMiliseconds } from '@playbookux/playbook-web-core';

import RangeQuestionType from './survey/range-question-type';
import TaskQuestionType from './survey/task-question-type';
import URLQuestionType from './survey/url-question-type';
import WrittenQuestionType from './survey/written-question-type';
import MultpleQuestionType from './survey/multiple-question-type';
import SingleQuestionType from './survey/single-question-type';

import {
  BottomButton,
  BottomButtonContainer,
  Container,
  Content,
  Control,
  ErrorMessage,
  Header,
  Recorder,
  RecordingSign,
  Step,
} from './styles';

const DISABLED_TIME = 500;
const LOADING_TIME = 500;

class ScreenRecorder extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentAnswer: null,
      disabled: true,
      duration: 0, // Duration in milliseconds
      loading: false,
      paused: false,
      pauseDuration: 0,
      pauseTime: null,
      startTime: Date.now(),
      step: 1,
    };

    this.timer = null;
  }

  async componentDidMount() {
    this.setupTimer();

    setTimeout(() => {
      this.setState({ disabled: false });
    }, DISABLED_TIME);
  }

  async componentWillUnmount() {
    this.clearTimer();
  }

  togglePause = () => {
    const { paused } = this.state;

    if (!paused) {
      this.setState({
        paused: true,
        pauseTime: Date.now(),
      }, () => {
        this.clearTimer();
      });
    } else {
      this.setState((state) => ({
        paused: false,
        pauseDuration: state.pauseDuration + (Date.now() - state.pauseTime),
      }), () => {
        this.setupTimer();
      });
    }
  };

  updateDuration = () => {
    this.setState((state) => ({
      duration: Date.now() - state.startTime - state.pauseDuration,
    }));
  }

  setupTimer = () => {
    this.updateDuration();

    this.timer = setInterval(() => {
      this.updateDuration();
    }, 500);
  };

  clearTimer = () => {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  handleNextTask = (lastStep) => {
    if (lastStep) {
      return;
    }

    this.setState((state) => ({
      step: state.step + 1,
    }));
  }

  onBottomButtonClicked = (lastStep) => () => {
    // Validation
    const { currentAnswer, loading } = this.state;

    if (loading) {
      return;
    }

    const { error } = currentAnswer;

    if (error) {
      this.setState({
        currentAnswer: {
          ...currentAnswer,
          touched: true,
        },
      });

      return;
    }

    this.setState({ loading: true });

    setTimeout(() => {
      this.setState({
        disabled: true,
        loading: false,
      });

      setTimeout(() => {
        this.setState({ disabled: false });
      }, DISABLED_TIME);

      this.handleNextTask(lastStep);
    }, LOADING_TIME);
  }

  onQuestionAnswered = (answer) => {
    this.setState({
      currentAnswer: {
        ...answer,
      },
    });
  }

  renderDynamicSurvey = (question) => {
    const props = {
      question,
      onQuestionAnswered: this.onQuestionAnswered,
    };

    switch (question.questionType) {
      case 'url':
      case 'image_select':
      case 'audio_video':
        return <URLQuestionType key={question.id} {...props} />;
      case 'task':
        return <TaskQuestionType key={question.id} {...props} />;
      case 'range':
        return <RangeQuestionType key={question.id} {...props} />;
      case 'written':
        return <WrittenQuestionType key={question.id} {...props} />;
      case 'multiple_select':
        return <MultpleQuestionType key={question.id} {...props} />;
      case 'single_select':
        return <SingleQuestionType key={question.id} {...props} />;
      default:
        return null;
    }
  };

  render() {
    const { survey } = this.props;
    const { questions } = survey;
    const steps = questions.length;

    const {
      currentAnswer,
      duration,
      paused,
      step,
      loading,
      disabled,
    } = this.state;

    const error = currentAnswer ? currentAnswer.error : null;
    const touched = currentAnswer ? currentAnswer.touched : null;

    const mmss = formatMiliseconds(duration);

    const lastStep = step === steps;

    return (
      <Container>
        <Header>
          <Recorder>
            <RecordingSign visible={!paused} />
            {mmss}
          </Recorder>
          <Step>{`${step} of ${steps}`}</Step>
          <Control onClick={this.togglePause}>
            <span>{paused ? 'Resume' : 'Pause'}</span>
          </Control>
        </Header>

        <Content>
          {this.renderDynamicSurvey(questions[step - 1])}
        </Content>

        <BottomButtonContainer>
          {touched && error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          <BottomButton
            loading={loading}
            disabled={disabled || loading || paused}
            onClick={this.onBottomButtonClicked(lastStep)}
          >
            {lastStep ? 'Finish' : 'Next'}
          </BottomButton>
        </BottomButtonContainer>
      </Container>
    );
  }
}

ScreenRecorder.propTypes = {
  survey: PropTypes.object.isRequired,
};

export default ScreenRecorder;
